/// <reference types="vite-plugin-svgr/client" />
import { useRef, useState } from 'react'
import { CheckIcon, Flex, UnstyledButton } from '@mantine/core'

import { isBudgetCategory } from '@/types/budgets.types'

import type { ManageBudgetsListItemProps } from '@/components/ManageBudgetsListItem/ManageBudgetsListItem.types'
import { NumberInput } from '@/components/NumberInput/NumberInput'
import { Text } from '@/components/Text/Text'

import { BUDGET_CATEGORIES } from '@/constants/budgetCategories.constants'

import { formatMoney } from '@/helpers/formatMoney.helper'
import { mapCategories } from '@/helpers/mapCategories.helper'
import { sanitizeEnum } from '@/helpers/sanitizeEnum.helper'

import PencilIcon from '@/assets/icons/pencil.svg?react'
import TrashIcon from '@/assets/icons/trash.svg?react'

import classes from './ManageBudgetsListItem.module.css'

export function ManageBudgetsListItem({
  addBudgetToDelete,
  addEditedMonthlyAmount,
  budget,
}: ManageBudgetsListItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [isDeleted, setIsDeleted] = useState(false)
  const [monthlyAmount, setMonthlyAmount] = useState(+budget.monthlyAmount)

  const monthlyAmountRef = useRef<HTMLInputElement>(null)

  const currentCategory = BUDGET_CATEGORIES.find(
    category => category.name === budget.category,
  )

  const handleMonthlyAmountChange = () => {
    const value = monthlyAmountRef.current?.value
      ? monthlyAmountRef.current?.value.replace(/,/g, '')
      : 0

    addEditedMonthlyAmount({
      budgetId: budget.id,
      value: +value,
    })

    setMonthlyAmount(+value!)
    setIsEditing(false)
  }

  const handleDeleteBudget = () => {
    addBudgetToDelete(budget.id)

    setIsDeleted(true)
  }

  if (!currentCategory || isDeleted) {
    return null
  }

  return (
    <Flex className={classes.container}>
      <Flex align="center">
        <Flex className={classes.icon}>
          <currentCategory.icon />
        </Flex>

        <Text align="left" ml={16} variant="body_4_semibold">
          {isBudgetCategory(budget.category)
            ? mapCategories(budget.category)
            : sanitizeEnum(budget.category)}
        </Text>
      </Flex>

      <Flex
        align="center"
        gap={{
          base: 8,
          md: 16,
        }}
        ml={8}
        pr={8}
      >
        {!isEditing ? (
          <>
            <Text mb={0} variant="body_1_semibold">
              {formatMoney(monthlyAmount, 'USD')}
            </Text>

            <UnstyledButton
              className={classes.actionButton}
              onClick={() => setIsEditing(true)}
            >
              <PencilIcon />
            </UnstyledButton>

            <UnstyledButton
              className={classes.actionButton}
              onClick={handleDeleteBudget}
            >
              <TrashIcon />
            </UnstyledButton>
          </>
        ) : (
          <>
            <NumberInput
              ref={monthlyAmountRef}
              hideControls
              m={0}
              value={monthlyAmount}
              w={100}
            />

            <UnstyledButton onClick={handleMonthlyAmountChange}>
              <CheckIcon width={12} />
            </UnstyledButton>
          </>
        )}
      </Flex>
    </Flex>
  )
}
